const RECENTLY_VIEWED_KEY = 'sealRecentlyViewedProducts';

class SealRecentlyViewed extends HTMLElement {
  connectedCallback() {
    this.carousel = this.querySelector('[data-seal-recently-viewed-carousel]');
    this.carouselTemplate = this.querySelector('[data-seal-recently-viewed-carousel-template]');
    this.currentHandle = this.dataset.currentProductHandle || '';
    this.limit = Number(this.dataset.limit || 4);
    this.sectionId = this.dataset.sectionId || this.closest('.shopify-section')?.id?.replace(/^shopify-section-/, '') || '';

    if (!this.carousel || !this.carouselTemplate || !this.sectionId) return;

    this.storeCurrentProduct();
    this.queueRender();
  }

  getHandles() {
    try {
      const parsed = JSON.parse(window.localStorage.getItem(RECENTLY_VIEWED_KEY) || '[]');
      return Array.isArray(parsed) ? parsed.filter(Boolean) : [];
    } catch (_error) {
      return [];
    }
  }

  setHandles(handles) {
    window.localStorage.setItem(RECENTLY_VIEWED_KEY, JSON.stringify(handles.slice(0, 12)));
  }

  storeCurrentProduct() {
    if (!this.currentHandle) return;

    const handles = this.getHandles().filter((handle) => handle !== this.currentHandle);
    handles.unshift(this.currentHandle);
    this.setHandles(handles);
  }

  queueRender() {
    const handles = this.getHandles().filter((handle) => handle !== this.currentHandle);
    if (!handles.length) {
      this.hidden = true;
      return;
    }

    const target = this.closest('.shopify-section') || this;
    if (!('IntersectionObserver' in window)) {
      this.render();
      return;
    }

    this.renderObserver = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.renderObserver?.disconnect();
        this.renderObserver = null;
        this.render();
      },
      { rootMargin: '1200px 0px' }
    );
    this.renderObserver.observe(target);
  }

  disconnectedCallback() {
    this.renderObserver?.disconnect();
    this.renderObserver = null;
  }

  async render() {
    const handles = this.getHandles()
      .filter((handle) => handle !== this.currentHandle)
      .slice(0, this.limit);

    if (!handles.length) {
      this.hidden = true;
      return;
    }

    const loadingCarousel = this.buildLoadingCarousel(handles.length);
    if (loadingCarousel) {
      this.carousel.replaceChildren(loadingCarousel);
      this.carousel.setAttribute('aria-busy', 'true');
      this.hidden = false;
      this.carousel.dispatchEvent(new CustomEvent('seal:product-carousel:updated', { bubbles: true }));
    }

    const cards = await Promise.all(handles.map((handle) => this.fetchProductCard(handle)));
    const validCards = cards.filter(Boolean);

    if (!validCards.length) {
      this.hidden = true;
      this.carousel.removeAttribute('aria-busy');
      return;
    }

    const productCarousel = this.buildCarousel(validCards);
    if (!productCarousel) {
      this.hidden = true;
      this.carousel.removeAttribute('aria-busy');
      return;
    }

    this.carousel.replaceChildren(productCarousel);
    this.carousel.removeAttribute('aria-busy');
    this.hidden = false;
    this.carousel.dispatchEvent(new CustomEvent('seal:product-carousel:updated', { bubbles: true }));
  }

  async fetchProductCard(handle) {
    try {
      const root = window.Shopify?.routes?.root || '/';
      const url = new URL(`${root}products/${encodeURIComponent(handle)}`, window.location.origin);
      const sectionIds = [this.sectionId, 'seal-recently-viewed-products'].filter(
        (sectionId, index, ids) => sectionId && ids.indexOf(sectionId) === index
      );

      for (const sectionId of sectionIds) {
        url.searchParams.set('section_id', sectionId);

        const response = await fetch(url.toString(), {
          credentials: 'same-origin',
        });
        if (!response.ok) continue;

        const text = await response.text();
        const card = await this.parseProductCard(text, handle);
        if (card) return card;
      }

      return null;
    } catch (_error) {
      return null;
    }
  }

  async parseProductCard(text, handle) {
    const html = new DOMParser().parseFromString(text, 'text/html');
    const { loadStyleModules } = await import('@theme/seal-style-modules');
    await loadStyleModules(html);
    const template = html.querySelector('[data-seal-recently-viewed-card-template]');
    if (!(template instanceof HTMLTemplateElement)) return null;

    const content = template.content.cloneNode(true);
    if (!(content instanceof DocumentFragment)) return null;

    const card = content.querySelector('[data-seal-recently-viewed-card]');
    if (!(card instanceof HTMLElement)) return null;

    this.prepareCard(card, handle);
    return card.outerHTML;
  }

  prepareCard(card, handle) {
    const safeHandle = handle.replace(/[^a-zA-Z0-9_-]/g, '-');

    const productCard = card.querySelector('product-card[id]');
    if (productCard) {
      productCard.id = `${productCard.id}-${safeHandle}`;
    }

    const productForm = card.querySelector('product-form-component[data-section-id]');
    if (productForm) {
      productForm.dataset.sectionId = this.sectionId;
    }

    const quickAddForm = card.querySelector('form[id^="QuickAdd-ProductForm-"]');
    if (quickAddForm) {
      quickAddForm.id = `${quickAddForm.id}-${safeHandle}`;
    }
  }

  buildCarousel(cards) {
    const fragment = this.carouselTemplate.content.cloneNode(true);
    if (!(fragment instanceof DocumentFragment)) return null;

    const wrapper = fragment.firstElementChild;
    if (!(wrapper instanceof HTMLElement)) return null;

    const slideshow = wrapper.querySelector('slideshow-component');
    const slidesContainer = wrapper.querySelector('slideshow-slides');
    if (!(slideshow instanceof HTMLElement) || !(slidesContainer instanceof HTMLElement)) return null;

    slidesContainer.innerHTML = cards.map((card, index) => this.renderSlide(card, index)).join('');
    slideshow.setAttribute('initial-slide', '0');
    slideshow.style.setProperty(
      '--slideshow-timeline',
      cards.map((_card, index) => `--slide-${index}`).join(',')
    );

    return wrapper;
  }

  buildLoadingCarousel(count) {
    const fragment = this.carouselTemplate.content.cloneNode(true);
    if (!(fragment instanceof DocumentFragment)) return null;

    const wrapper = fragment.firstElementChild;
    if (!(wrapper instanceof HTMLElement)) return null;

    const slideshow = wrapper.querySelector('slideshow-component');
    const slidesContainer = wrapper.querySelector('slideshow-slides');
    if (!(slideshow instanceof HTMLElement) || !(slidesContainer instanceof HTMLElement)) return null;

    const placeholders = Array.from({ length: Math.max(1, Math.min(count, this.limit)) }, (_item, index) => {
      return this.renderSlide(this.renderLoadingCard(), index);
    });

    slidesContainer.innerHTML = placeholders.join('');
    slideshow.setAttribute('initial-slide', '0');
    slideshow.style.setProperty(
      '--slideshow-timeline',
      placeholders.map((_card, index) => `--slide-${index}`).join(',')
    );

    return wrapper;
  }

  renderSlide(card, index) {
    return `
      <slideshow-slide
        ref="slides[]"
        aria-hidden="${index === 0 ? 'false' : 'true'}"
        style="--slideshow-timeline: --slide-${index}; --product-media-fit: cover; --grid-template-rows: 50dvh 1fr; --grid-template-rows-desktop: 70dvh 1fr;"
        class="resource-list__slide"
      >
        ${card}
      </slideshow-slide>
    `;
  }

  renderLoadingCard() {
    return `
      <div class="resource-list__item seal-recently-viewed__skeleton-card" aria-hidden="true">
        <div class="seal-recently-viewed__skeleton-media"></div>
        <div class="seal-recently-viewed__skeleton-line"></div>
        <div class="seal-recently-viewed__skeleton-line seal-recently-viewed__skeleton-line--short"></div>
      </div>
    `;
  }
}

if (!customElements.get('seal-recently-viewed')) {
  customElements.define('seal-recently-viewed', SealRecentlyViewed);
}
